import { message } from 'antd'
import api, { apiPost, apiGet } from '@shared/api/api'
import type {
  LoginRequest,
  AuthResponse,
  RefreshRequest,
  User,
  RegisterRequest,
  RegisterResponse,
  OAuthProvidersResponse,
  CompleteRegistrationRequest,
} from '../types/auth'
import {
  clearStoredAuthSession,
  getStoredAccessToken,
  getStoredActiveOrganizationId,
  getStoredExpiresAt,
  getStoredRefreshToken,
  getStoredUser,
  removeStoredActiveOrganizationId,
  setStoredAccessToken,
  setStoredActiveOrganizationId,
  setStoredExpiresAt,
  setStoredRefreshToken,
  setStoredUser,
} from '../utils/sessionStorage'
import {
  getImpersonationReturnPath,
  isImpersonationActive,
  restoreAdminSession,
} from '../utils/impersonationStorage'

const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REFRESH: '/auth/refresh',
  REGISTER: '/auth/register',
  COMPLETE_REGISTRATION: '/auth/complete-registration',
  ME: '/auth/me',
  OAUTH_PROVIDERS: '/auth/oauth/providers',
  OAUTH_GOOGLE: '/auth/oauth/google',
  OAUTH_GOOGLE_CODE: '/auth/oauth/google/code',
  OAUTH_APPLE: '/auth/oauth/apple',
} as const

const syncActiveOrganization = (user: User): void => {
  if (user.organization_id) {
    setStoredActiveOrganizationId(user.organization_id)
    return
  }

  removeStoredActiveOrganizationId()
}

let refreshInFlight: Promise<AuthResponse> | null = null

// Ответы точек входа не ретраятся рефрешем: 401 от /auth/refresh означает
// конец сессии, а 401 от /auth/login — неверный пароль.
const AUTH_NO_RETRY_ENDPOINTS = [
  AUTH_ENDPOINTS.LOGIN,
  AUTH_ENDPOINTS.REFRESH,
  AUTH_ENDPOINTS.REGISTER,
  AUTH_ENDPOINTS.OAUTH_GOOGLE,
  AUTH_ENDPOINTS.OAUTH_GOOGLE_CODE,
  AUTH_ENDPOINTS.OAUTH_APPLE,
]

export const isAuthEndpoint = (url?: string): boolean =>
  Boolean(url) && AUTH_NO_RETRY_ENDPOINTS.some(endpoint => url!.endsWith(endpoint))

export const authService = {
  login: (data: LoginRequest): Promise<AuthResponse> => {
    return apiPost<AuthResponse>(AUTH_ENDPOINTS.LOGIN, data)
  },

  register: (data: RegisterRequest): Promise<RegisterResponse> => {
    return apiPost<RegisterResponse>(AUTH_ENDPOINTS.REGISTER, data)
  },

  completeRegistration: (data: CompleteRegistrationRequest): Promise<RegisterResponse> => {
    return apiPost<RegisterResponse>(AUTH_ENDPOINTS.COMPLETE_REGISTRATION, data)
  },

  getOAuthProviders: (): Promise<OAuthProvidersResponse> => {
    return apiGet<OAuthProvidersResponse>(AUTH_ENDPOINTS.OAUTH_PROVIDERS)
  },

  oauthGoogleCode: (code: string): Promise<AuthResponse> => {
    return apiPost<AuthResponse>(AUTH_ENDPOINTS.OAUTH_GOOGLE_CODE, { code })
  },

  oauthGoogle: (idToken: string): Promise<AuthResponse> => {
    return apiPost<AuthResponse>(AUTH_ENDPOINTS.OAUTH_GOOGLE, { id_token: idToken })
  },

  oauthApple: (
    idToken: string,
    fullName?: { firstName?: string; lastName?: string }
  ): Promise<AuthResponse> => {
    return apiPost<AuthResponse>(AUTH_ENDPOINTS.OAUTH_APPLE, {
      id_token: idToken,
      first_name: fullName?.firstName,
      last_name: fullName?.lastName,
    })
  },

  refresh: (data: RefreshRequest): Promise<AuthResponse> => {
    return apiPost<AuthResponse>(AUTH_ENDPOINTS.REFRESH, data)
  },

  // Единственная точка обновления сессии: параллельные 401 делят один
  // запрос к /auth/refresh, а отказ рефреша сразу завершает сессию —
  // без этого каждый 401 плодил свой рефреш, а 401 от самого рефреша
  // уходил в бесконечный цикл.
  refreshSession: (): Promise<AuthResponse> => {
    if (refreshInFlight) {
      return refreshInFlight
    }
    const refreshToken = getStoredRefreshToken()
    if (!refreshToken) {
      return Promise.reject(new Error('no refresh token'))
    }
    refreshInFlight = authService
      .refresh({ refresh_token: refreshToken })
      .then(session => {
        authService.saveSession(session)
        return session
      })
      .catch(error => {
        authService.logout()
        throw error
      })
      .finally(() => {
        refreshInFlight = null
      })
    return refreshInFlight
  },

  getMe: (): Promise<User> => {
    return apiGet<User>(AUTH_ENDPOINTS.ME)
  },

  logout: (): void => {
    clearStoredAuthSession()
  },

  saveSession: (response: AuthResponse): void => {
    setStoredAccessToken(response.access_token)
    setStoredRefreshToken(response.refresh_token)
    setStoredExpiresAt(response.expires_at.toString())
    setStoredUser(response.user)
    syncActiveOrganization(response.user)
  },

  getAccessToken: (): string | null => {
    return getStoredAccessToken()
  },

  getRefreshToken: (): string | null => {
    return getStoredRefreshToken()
  },

  getStoredUser: (): User | null => {
    return getStoredUser()
  },

  saveUser: (user: User): void => {
    setStoredUser(user)
    syncActiveOrganization(user)
  },

  getActiveOrganizationId: (): string | null => {
    return getStoredActiveOrganizationId()
  },

  setActiveOrganizationId: (organizationId: string | null): void => {
    if (organizationId) {
      setStoredActiveOrganizationId(organizationId)
      return
    }

    removeStoredActiveOrganizationId()
  },

  isTokenExpired: (): boolean => {
    const expiresAt = getStoredExpiresAt()
    if (!expiresAt) return true
    return Date.now() >= parseInt(expiresAt, 10) * 1000
  },

  setupInterceptors: (onUnauthorized: () => void): number => {
    return api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config

        if (isImpersonationActive()) {
          // Imp-токен не рефрешится: по истечении — возврат в админку.
          if (error.response?.status === 401) {
            const returnPath = getImpersonationReturnPath()
            restoreAdminSession()
            window.location.assign(returnPath)
            return Promise.reject(error)
          }
          if (error.response?.status === 403) {
            const text =
              (error.response?.data as { message?: string } | undefined)?.message ||
              'Режим просмотра — только чтение'
            message.error(text)
            return Promise.reject(error)
          }
        }

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isAuthEndpoint(originalRequest.url)
        ) {
          originalRequest._retry = true

          try {
            const response = await authService.refreshSession()
            originalRequest.headers.Authorization = `Bearer ${response.access_token}`
            const activeOrganizationId = authService.getActiveOrganizationId()
            const explicitOrganizationHeader =
              originalRequest.headers?.['X-Organization-ID'] ??
              originalRequest.headers?.['x-organization-id']
            if (activeOrganizationId && !explicitOrganizationHeader) {
              originalRequest.headers['X-Organization-ID'] = activeOrganizationId
            }
            return api(originalRequest)
          } catch {
            // refreshSession уже очистил сессию.
            onUnauthorized()
          }
        }

        return Promise.reject(error)
      }
    )
  },

  ejectInterceptor: (id: number): void => {
    api.interceptors.response.eject(id)
  },
}
